import { formatMoney } from 'accounting';

/**
 * Difference overall formats.
 */
const SYMBOL_VALUE = '%s%v';
const SYMBOL_SPACE_VALUE = '%s %v';
const VALUE_SPACE_SYMBOL = '%v %s';

/**
 * Country-specific settings
 * Shows a map of all country codes we support
 * showCode true will prefix Currency Code separately from Currency Symbol
 * this mainly applies to NA and APAC countries
 */
const COUNTRY_FORMAT = {
  at: {
    /* no settings */
  },
  au: { showCode: true },
  be: {
    /* no settings */
  },
  ca: { showCode: true },
  ch: {
    /* no settings */
  },
  cn: { showCode: true },
  de: {
    /* no settings */
  },
  dk: {
    /* no settings */
  },
  es: {
    /* no settings */
  },
  fi: {
    /* no settings */
  },
  fr: {
    /* no settings */
  },
  gb: {
    /* no settings */
  },
  hk: { showCode: true },
  ie: {
    /* no settings */
  },
  it: {
    /* no settings */
  },
  jp: { showCode: true },
  nl: {
    /* no settings */
  },
  no: {
    /* no settings */
  },
  nz: { showCode: true },
  se: {
    /* no settings */
  },
  sg: { showCode: true },
  us: { showCode: true },
  ue: {
    /* no settings */
  },
};

/**
 * Currency default settings
 * Shows a map of all currency codes we support
 */
const CURRENCY_DEFAULT = {
  AUD: { symbol: '$', precision: 2 },
  CAD: { symbol: '$', precision: 2 },
  CHF: { symbol: 'CHF', precision: 2 },
  CNY: { symbol: '¥', precision: 2 },
  DKK: { symbol: 'DKK', precision: 2 },
  EUR: { symbol: '€', precision: 2 },
  GBP: { symbol: '£', precision: 2 },
  HKD: { symbol: '$', precision: 2 },
  JPY: { symbol: '¥', precision: 0 },
  NOK: { symbol: 'NOK', precision: 2 },
  NZD: { symbol: '$', precision: 2 },
  SEK: { symbol: 'SEK', precision: 2 },
  SGD: { symbol: '$', precision: 2 },
  USD: { symbol: '$', precision: 2 },
};

/**
 * Language-specific default settings and
 * Language Currency-specific overrides from currency default
 * Shows a map of all language codes we support
 */
const LANGUAGE_FORMAT = {
  de: {
    DEFAULT: { format: VALUE_SPACE_SYMBOL, thousand: '.', decimal: ',' },
  },
  en: {
    DEFAULT: { format: SYMBOL_VALUE, thousand: ',', decimal: '.' },
    CHF: { format: SYMBOL_SPACE_VALUE },
    DKK: { format: SYMBOL_SPACE_VALUE },
    EUR: { format: SYMBOL_SPACE_VALUE },
    NOK: { format: SYMBOL_SPACE_VALUE },
    SEK: { format: SYMBOL_SPACE_VALUE },
  },
  es: {
    DEFAULT: { format: VALUE_SPACE_SYMBOL, thousand: '.', decimal: ',' },
    GBP: { symbol: 'GBP' },
    USD: { symbol: 'US$' },
  },
  fr: {
    DEFAULT: { format: VALUE_SPACE_SYMBOL, thousand: ' ', decimal: ',' },
    GBP: { symbol: '£GB' },
    USD: { symbol: '$' },
  },
  it: {
    DEFAULT: { format: VALUE_SPACE_SYMBOL, thousand: '.', decimal: ',' },
    USD: { symbol: 'USD' },
  },
  nl: {
    DEFAULT: { format: SYMBOL_SPACE_VALUE, thousand: '.', decimal: ',' },
    USD: { symbol: 'US$' },
  },
  sv: {
    DEFAULT: { format: VALUE_SPACE_SYMBOL, thousand: ' ', decimal: ',' },
    DKK: { symbol: 'Dkr' },
    GBP: { symbol: 'GBP' },
    NOK: { symbol: 'Nkr' },
    SEK: { symbol: 'kr' },
    USD: { symbol: 'US$' },
  },
};

/**
 * Suffixes to use when tax is included.
 */
const INCLUDING_TAX_SUFFIXES = {
  de: 'inkl MwSt',
  en: 'inc. VAT',
  es: 'con IVA',
  fr: 'TTC',
  it: 'IVA incl.',
  nl: 'incl. BTW',
  sv: 'inkl. moms',
};

/**
 * Suffixes to use when tax is not included.
 */
const EXCLUDING_TAX_SUFFIXES = {
  de: 'exkl MwSt',
  en: 'ex. VAT',
  es: 'sin IVA',
  fr: 'HT',
  it: 'IVA escl.',
  nl: 'ex BTW',
  sv: 'exkl. moms',
};

/**
 * helper function to only prefix currency code when currency code != symbol
 * @param  {Object} format //format settings object
 * @param {String} currencyCode //Example: USD
 * */
const showCodeHelper = (format, currencyCode) => {
  if (format.showCode && format.symbol == currencyCode) format.showCode = false;

  return format;
};

/**
 * currency formatter based on locale and currency code
 * @param {Number} value //price value
 * @param {String} locale //Example: en_US
 * @param {String} currencyCode //Example: USD
 * @param {boolean} tax //show tax suffix
 * */
export const currencyFormat = (value, locale, currencyCode, tax) => {
  // Determine the correct format from the country and currency code.
  const [language, country] = locale.toLowerCase().split('_');
  const format = showCodeHelper(
    {
      ...CURRENCY_DEFAULT[currencyCode], // default currency settings
      ...(LANGUAGE_FORMAT[language] ? LANGUAGE_FORMAT[language].DEFAULT : {}), // language default settings
      ...(LANGUAGE_FORMAT[language]
        ? LANGUAGE_FORMAT[language][currencyCode]
        : {}), // language currency-specific settings
      ...COUNTRY_FORMAT[country], // country-specific settings
    },
    currencyCode
  );

  // Format the value according to the chosen format.
  const newValue = formatMoney(value, format);

  // Only show the currency code if the region requires it.
  const prefix = format.showCode ? currencyCode : '';
  const suffix =
    tax === true
      ? INCLUDING_TAX_SUFFIXES[language]
      : tax === false && EXCLUDING_TAX_SUFFIXES[language];

  return {
    value: newValue,
    prefix,
    suffix,
  };
};

export const currencySymbol = (currencyCode) =>
  CURRENCY_DEFAULT[currencyCode]?.symbol;
